import { useState, useEffect } from 'react';
import { View, Text, ScrollView, Pressable, Alert, ActivityIndicator, Modal, Platform } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useAuthStore } from '../../../../store/authStore';
import { authApi, AddressSearchResult } from '../../../../shared/utils/api';
import { useStyles } from '../../../../hooks/useStyles';
import { useResponsive, getResponsiveValue } from '../../../../shared/utils/responsive';
import { addColorOpacity } from '../../../../shared/utils/styles';
import { formatAddress } from '../../../../shared/utils/address';
import { getDisplayName } from '../../../../shared/utils/user';
import { PortfolioDetail } from '../../../../shared/types/portfolio';
import { Property, PropertyDetail, PortfolioView } from '../../../../shared/types/property';
import { PortfolioFinancialSummaryResponse } from '../../../../shared/types/financial';
import { CommentSection } from '../../../../components/comments';
import { PropertyMap, AddressSearch } from '../../../../components/map';
import { Button, Input, BackButton, StatusBadge, TabBar, Tab, OwnershipBar, SimpleOwnershipBar } from '../../../../components/ui';
import { SectionCard, DetailCard, Card } from '../../../../components/ui';
import { PageContainer, PageHeader } from '../../../../components/layout';
import { logger } from '../../../../shared/utils/logger';
import { Ionicons } from '@expo/vector-icons';

// Human-friendly labels for property views and statuses
const VIEW_LABELS: Record<PortfolioView, string> = {
  current: 'Owned',
  pipeline: 'Looking At',
  full: 'All',
  historical: 'History',
};

const STATUS_LABELS: Record<Property['status'], string> = {
  research: 'Looking At',
  under_contract: 'Buying',
  owned: 'Owned',
  listed_for_sale: 'Selling',
  sold: 'Sold',
};

export default function PortfolioDetailPage() {
  const { id, threadId } = useLocalSearchParams<{ id: string; threadId?: string }>();
  const [portfolio, setPortfolio] = useState<PortfolioDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  
  // Properties state
  const [properties, setProperties] = useState<Property[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [propertyView, setPropertyView] = useState<PortfolioView>('current');
  const [createPropertyModalVisible, setCreatePropertyModalVisible] = useState(false);
  const [newPropertyAddress, setNewPropertyAddress] = useState('');
  const [newPropertyStreetAddress, setNewPropertyStreetAddress] = useState('');
  const [newPropertyCity, setNewPropertyCity] = useState('');
  const [newPropertyState, setNewPropertyState] = useState('');
  const [newPropertyZipCode, setNewPropertyZipCode] = useState('');
  const [newPropertyCounty, setNewPropertyCounty] = useState('');
  const [newPropertyLatitude, setNewPropertyLatitude] = useState<number | null>(null);
  const [newPropertyLongitude, setNewPropertyLongitude] = useState<number | null>(null);
  const [newPropertyValue, setNewPropertyValue] = useState('');
  const [newPropertyStatus, setNewPropertyStatus] = useState<Property['status']>('research');
  const [creatingProperty, setCreatingProperty] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [addressSearchKey, setAddressSearchKey] = useState(0); // Key to reset AddressSearch
  
  // Remove property modal
  const [removePropertyModalVisible, setRemovePropertyModalVisible] = useState(false);
  const [propertyToRemove, setPropertyToRemove] = useState<{ id: number; address: string } | null>(null);
  const [removingProperty, setRemovingProperty] = useState(false);
  
  // Financial summary
  const [financialSummary, setFinancialSummary] = useState<PortfolioFinancialSummaryResponse | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  
  // Tab navigation
  const [activeTab, setActiveTab] = useState<'dashboard' | 'properties' | 'partners' | 'discussion'>('dashboard');
  
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const styles = useStyles();
  const { tokens, utils } = styles;
  const responsive = useResponsive();
  
  // Responsive values for typography
  const bodySize = getResponsiveValue(15, 15, 16, 16);

  useEffect(() => {
    if (id) {
      loadPortfolio();
      loadProperties();
      loadFinancialSummary();
    }
  }, [id, propertyView]);

  const loadPortfolio = async () => {
    try {
      setLoading(true);
      const portfolioId = parseInt(id as string, 10);
      if (isNaN(portfolioId) || portfolioId <= 0) {
        Alert.alert('Error', 'Invalid portfolio ID');
        router.back();
        return;
      }

      const response = await authApi.getPortfolio(portfolioId);
      setPortfolio(response.portfolio);
    } catch (error: any) {
      // Sanitize error messages - show generic messages to prevent information leakage
      if (error.status === 403) {
        Alert.alert('Access Denied', 'You do not have access to this portfolio.');
      } else if (error.status === 404) {
        Alert.alert('Not Found', 'Portfolio not found.');
      } else {
        Alert.alert('Error', 'Failed to load portfolio. Please try again.');
      }
      router.back();
    } finally {
      setLoading(false);
    }
  };

  const handleInvitePartner = async () => {
    // Validate and sanitize email
    const trimmedEmail = inviteEmail.trim();
    
    if (!trimmedEmail) {
      Alert.alert('Error', 'Email is required');
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      Alert.alert('Error', 'Please enter a valid email address');
      return;
    }

    // Length limit for security
    if (trimmedEmail.length > 255) {
      Alert.alert('Error', 'Email address is too long');
      return;
    }

    setInviting(true);
    try {
      const portfolioId = parseInt(id as string, 10);
      if (isNaN(portfolioId) || portfolioId <= 0) {
        Alert.alert('Error', 'Invalid portfolio ID');
        return;
      }
      
      await authApi.invitePartner(portfolioId, {
        email: trimmedEmail,
      });
      setInviteModalVisible(false);
      setInviteEmail('');
      await loadPortfolio();
      Alert.alert('Success', 'Partner invited successfully. Note: Ownership is set at the property level.');
    } catch (error: any) {
      // Sanitize error messages - show generic message to prevent information leakage
      const safeMessage = 'Failed to invite partner. Please try again.';
      Alert.alert('Error', safeMessage);
    } finally {
      setInviting(false);
    }
  };

  const loadProperties = async (view?: PortfolioView) => {
    try {
      setLoadingProperties(true);
      const portfolioId = parseInt(id as string, 10);
      if (isNaN(portfolioId) || portfolioId <= 0) return;
      
      const viewToUse = view || propertyView;
      const response = await authApi.getProperties(portfolioId, viewToUse);
      const loadedProperties = response.properties;
      
      // Auto-switch logic: If current view is empty but other views have properties, switch to the first available view
      if (viewToUse === 'current' && loadedProperties.length === 0) {
        // Check other views in priority order: pipeline, full, historical
        const viewsToCheck: PortfolioView[] = ['pipeline', 'full', 'historical'];
        for (const checkView of viewsToCheck) {
          try {
            const checkResponse = await authApi.getProperties(portfolioId, checkView);
            if (checkResponse.properties.length > 0) {
              setPropertyView(checkView);
              setProperties(checkResponse.properties);
              return;
            }
          } catch (e) {
            // Continue checking other views
          }
        }
      }
      
      setProperties(loadedProperties);
    } catch (error: any) {
      // Sanitize error messages - show generic message to prevent information leakage
      Alert.alert('Error', 'Failed to load properties. Please try again.');
    } finally {
      setLoadingProperties(false);
    }
  };

  const loadFinancialSummary = async () => {
    try {
      setLoadingSummary(true);
      const portfolioId = parseInt(id as string, 10);
      if (isNaN(portfolioId) || portfolioId <= 0) return;
      
      const response = await authApi.getPortfolioFinancialSummary(portfolioId);
      setFinancialSummary(response);
    } catch (error: any) {
      // Silently fail - summary is optional
      // Only log in development - don't expose financial errors in production
      logger.error('Failed to load financial summary', error, 'portfolio');
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleRemovePropertyClick = (propertyId: number, propertyAddress: string) => {
    setPropertyToRemove({ id: propertyId, address: propertyAddress });
    setRemovePropertyModalVisible(true);
  };

  const handleConfirmRemoveProperty = async () => {
    if (!propertyToRemove) return;

    setRemovingProperty(true);
    try {
      const portfolioId = parseInt(id as string, 10);
      if (isNaN(portfolioId) || portfolioId <= 0) {
        Alert.alert('Error', 'Invalid portfolio ID');
        return;
      }
      
      // Validate property ID
      if (!propertyToRemove.id || typeof propertyToRemove.id !== 'number' || propertyToRemove.id <= 0) {
        Alert.alert('Error', 'Invalid property ID');
        return;
      }
      
      await authApi.deleteProperty(portfolioId, propertyToRemove.id);
      Alert.alert('Success', 'Property removed successfully.');
      setRemovePropertyModalVisible(false);
      setPropertyToRemove(null);
      loadProperties(); // Reload with current view (will auto-switch if needed)
      loadPortfolio(); // Reload portfolio to update ownership
      loadFinancialSummary(); // Reload financial summary
    } catch (error: any) {
      // Sanitize error messages - show generic message to prevent information leakage
      Alert.alert('Error', 'Failed to remove property. Please try again.');
    } finally {
      setRemovingProperty(false);
    }
  };

  const handleCreateProperty = async () => {
    // Validate and sanitize input - require street address
    const trimmedStreetAddress = newPropertyStreetAddress.trim();
    
    if (!trimmedStreetAddress) {
      Alert.alert('Validation Error', 'Street address is required.');
      return;
    }

    // Client-side length validation (defense in depth)
    if (trimmedStreetAddress.length > 500) {
      Alert.alert('Validation Error', 'Street address must be 500 characters or less.');
      return;
    }

    const trimmedValue = newPropertyValue.trim();
    const propertyValue = trimmedValue ? parseFloat(trimmedValue) : null;
    if (propertyValue !== null && (isNaN(propertyValue) || propertyValue < 0)) {
      Alert.alert('Validation Error', 'Property value must be a positive number.');
      return;
    }

    // Validate property value is not too large (prevent overflow)
    if (propertyValue !== null && propertyValue > 999999999999) {
      Alert.alert('Validation Error', 'Property value is too large.');
      return;
    }

    setCreatingProperty(true);
    try {
      const portfolioId = parseInt(id as string, 10);
      if (isNaN(portfolioId) || portfolioId <= 0) {
        Alert.alert('Error', 'Invalid portfolio ID');
        return;
      }
      
      const response = await authApi.createProperty(portfolioId, {
        street_address: trimmedStreetAddress,
        city: newPropertyCity.trim() || undefined,
        state: newPropertyState.trim() || undefined,
        zip_code: newPropertyZipCode.trim() || undefined,
        county: newPropertyCounty.trim() || undefined,
        latitude: newPropertyLatitude || undefined,
        longitude: newPropertyLongitude || undefined,
        property_value: propertyValue,
        status: newPropertyStatus,
      });
      Alert.alert('Success', 'Property created successfully!');
      setCreatePropertyModalVisible(false);
      // Reset all fields
      setNewPropertyAddress('');
      setNewPropertyStreetAddress('');
      setNewPropertyCity('');
      setNewPropertyState('');
      setNewPropertyZipCode('');
      setNewPropertyCounty('');
      setNewPropertyLatitude(null);
      setNewPropertyLongitude(null);
      setNewPropertyValue('');
      setNewPropertyStatus('research');
      setShowManualEntry(false);
      setAddressSearchKey(prev => prev + 1); // Reset AddressSearch
      loadProperties();
      loadPortfolio(); // Reload to update ownership calculations
    } catch (error: any) {
      // Sanitize error messages - show generic message to prevent information leakage
      const errorMessage = error?.message || 'Failed to create property';
      // Only show specific validation errors, otherwise generic message
      const safeMessage = errorMessage.includes('Validation error') || errorMessage.includes('required') || errorMessage.includes('length')
        ? errorMessage
        : 'Failed to create property. Please try again.';
      Alert.alert('Error', safeMessage);
    } finally {
      setCreatingProperty(false);
    }
  };

  const handleRemovePartner = (partnerUserId: number, partnerName: string) => {
    // Validate inputs to prevent path traversal
    if (!partnerUserId || typeof partnerUserId !== 'number' || isNaN(partnerUserId) || partnerUserId <= 0) {
      Alert.alert('Error', 'Invalid partner');
      return;
    }
    
    Alert.alert(
      'Remove Partner',
      `Are you sure you want to remove ${partnerName} from this portfolio?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              const portfolioId = parseInt(id as string, 10);
              if (isNaN(portfolioId) || portfolioId <= 0) {
                Alert.alert('Error', 'Invalid portfolio ID');
                return;
              }
              
              await authApi.removePartner(portfolioId, partnerUserId);
              await loadPortfolio();
              Alert.alert('Success', 'Partner removed successfully');
            } catch (error: any) {
              // Sanitize error messages - show generic message to prevent information leakage
              Alert.alert('Error', 'Failed to remove partner. Please try again.');
            }
          },
        },
      ]
    );
  };

  if (loading) {
    return (
      <PageContainer scrollable={false}>
        <View style={{ 
          flex: 1, 
          justifyContent: 'center', 
          alignItems: 'center',
        }}>
          <ActivityIndicator size="large" color={tokens.color.primary} />
          <Text style={[styles.text('base'), { marginTop: utils.spacing.md, color: addColorOpacity(tokens.color.onBackground, 0.8) }]}>
            Loading portfolio...
          </Text>
        </View>
      </PageContainer>
    );
  }

  if (!portfolio) {
    return (
      <PageContainer>
        <Text style={[styles.heading('xl'), { color: tokens.color.error, marginBottom: utils.spacing.lg }]}>
          Portfolio not found
        </Text>
        <BackButton to="/(tabs)/portfolios" />
      </PageContainer>
    );
  }

  // Calculate user's ownership and stats
  const userPartner = portfolio.partners.find(p => p.user_id === user?.id);
  const userOwnership = userPartner?.ownership_percentage || 0;
  
  // Calculate values by status
  const ownedProperties = properties.filter(p => p.status === 'owned');
  const researchProperties = properties.filter(p => p.status === 'research');
  const underContractProperties = properties.filter(p => p.status === 'under_contract');
  const listedProperties = properties.filter(p => p.status === 'listed_for_sale');
  const soldProperties = properties.filter(p => p.status === 'sold');
  
  const ownedValue = ownedProperties.reduce((sum, p) => sum + (p.property_value || 0), 0);
  const researchValue = researchProperties.reduce((sum, p) => sum + (p.property_value || 0), 0);
  const pipelineValue = underContractProperties.reduce((sum, p) => sum + (p.property_value || 0), 0);
  const listedValue = listedProperties.reduce((sum, p) => sum + (p.property_value || 0), 0);
  
  const totalPortfolioValue = ownedValue; // Only count owned as "actual" portfolio value
  const totalPotentialValue = ownedValue + researchValue + pipelineValue + listedValue;
  const userShareValue = totalPortfolioValue * (userOwnership / 100);
  
  // Count properties by status
  const ownedCount = ownedProperties.length;
  const lookingAtCount = researchProperties.length + underContractProperties.length;

  // Define tabs
  const tabs: Tab[] = [
    { key: 'dashboard', label: 'Dashboard', icon: 'grid-outline' },
    { key: 'properties', label: 'Properties', icon: 'home-outline', badge: properties.length || undefined },
    { key: 'partners', label: 'Partners', icon: 'people-outline', badge: portfolio.partners.length || undefined },
    { key: 'discussion', label: 'Discussion', icon: 'chatbubbles-outline' },
  ];

  return (
    <PageContainer verticalPadding="2xl">
      <PageHeader
        title={portfolio.name}
        subtitle={portfolio.description || undefined}
        backButton={<BackButton to="/(tabs)/portfolios" />}
      />

      {/* Tab Navigation */}
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as typeof activeTab)}
      />

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <>
          {/* Quick Stats */}
      <View style={{
        flexDirection: responsive.isMobile ? 'column' : 'row',
        gap: utils.spacing.md,
        marginBottom: utils.spacing.xl,
      }}>
        {/* Total Value Card */}
        <View style={{
          flex: 1,
          backgroundColor: tokens.color.surface,
          borderRadius: utils.radius.lg,
          padding: utils.spacing.lg,
          borderWidth: 1,
          borderColor: tokens.color.border,
          ...Platform.select({
            web: {
              boxShadow: `0 2px 8px ${addColorOpacity('#000', 0.06)}`,
            } as any,
            default: {
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.06,
              shadowRadius: 8,
              elevation: 2,
            },
          }),
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: utils.spacing.sm }}>
            <View style={{
              width: 36,
              height: 36,
              borderRadius: utils.radius.md,
              backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: utils.spacing.sm,
            }}>
              <Ionicons name="home" size={18} color={tokens.color.primary} />
            </View>
            <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.6) }]}>
              Portfolio Value
            </Text>
          </View>
          <Text style={[styles.heading('xl'), { color: tokens.color.onSurface, marginBottom: utils.spacing.xs }]}>
            ${totalPortfolioValue.toLocaleString()}
          </Text>
          <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5), marginBottom: utils.spacing.xs }]}>
            {ownedCount} owned {ownedCount === 1 ? 'property' : 'properties'}
          </Text>
          
          {/* Value breakdown by status */}
          {(researchValue > 0 || pipelineValue > 0 || listedValue > 0) && (
            <View style={{ marginTop: utils.spacing.sm, paddingTop: utils.spacing.sm, borderTopWidth: 1, borderTopColor: addColorOpacity(tokens.color.border, 0.5) }}>
              <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.4), marginBottom: utils.spacing.xs }]}>
                Additional Pipeline
              </Text>
              {researchValue > 0 && (
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                  <Text style={[styles.text('xs'), { color: '#3B82F6' }]}>
                    Researching ({researchProperties.length})
                  </Text>
                  <Text style={[styles.text('xs'), { color: '#3B82F6' }]}>
                    ${researchValue.toLocaleString()}
                  </Text>
                </View>
              )}
              {pipelineValue > 0 && (
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                  <Text style={[styles.text('xs'), { color: tokens.color.warning }]}>
                    Under Contract ({underContractProperties.length})
                  </Text>
                  <Text style={[styles.text('xs'), { color: tokens.color.warning }]}>
                    ${pipelineValue.toLocaleString()}
                  </Text>
                </View>
              )}
              {listedValue > 0 && (
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                  <Text style={[styles.text('xs'), { color: tokens.color.error }]}>
                    Listed for Sale ({listedProperties.length})
                  </Text>
                  <Text style={[styles.text('xs'), { color: tokens.color.error }]}>
                    ${listedValue.toLocaleString()}
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>

        {/* Your Share Card */}
        <View style={{
          flex: 1,
          backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
          borderRadius: utils.radius.lg,
          padding: utils.spacing.lg,
          borderWidth: 2,
          borderColor: addColorOpacity(tokens.color.primary, 0.2),
          ...Platform.select({
            web: {
              boxShadow: `0 2px 8px ${addColorOpacity(tokens.color.primary, 0.1)}`,
            } as any,
            default: {
              shadowColor: tokens.color.primary,
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 8,
              elevation: 2,
            },
          }),
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: utils.spacing.sm }}>
            <View style={{
              width: 36,
              height: 36,
              borderRadius: utils.radius.md,
              backgroundColor: addColorOpacity(tokens.color.primary, 0.15),
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: utils.spacing.sm,
            }}>
              <Ionicons name="person" size={18} color={tokens.color.primary} />
            </View>
            <Text style={[styles.text('sm'), { color: tokens.color.primary, fontWeight: utils.typography.weights.semibold }]}>
              Your Share
            </Text>
          </View>
          <Text style={[styles.heading('xl'), { color: tokens.color.primary, marginBottom: utils.spacing.xs }]}>
            ${userShareValue.toLocaleString()}
          </Text>
          <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.primary, 0.7) }]}>
            {userOwnership > 0 ? `${userOwnership.toFixed(1)}% ownership` : 'Set ownership in properties'}
          </Text>
        </View>

        {/* Property Status Card */}
        <View style={{
          flex: 1,
          backgroundColor: tokens.color.surface,
          borderRadius: utils.radius.lg,
          padding: utils.spacing.lg,
          borderWidth: 1,
          borderColor: tokens.color.border,
          ...Platform.select({
            web: {
              boxShadow: `0 2px 8px ${addColorOpacity('#000', 0.06)}`,
            } as any,
            default: {
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.06,
              shadowRadius: 8,
              elevation: 2,
            },
          }),
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: utils.spacing.sm }}>
            <View style={{
              width: 36,
              height: 36,
              borderRadius: utils.radius.md,
              backgroundColor: addColorOpacity(tokens.color.success, 0.1),
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: utils.spacing.sm,
            }}>
              <Ionicons name="checkmark-circle" size={18} color={tokens.color.success} />
            </View>
            <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.6) }]}>
              Properties
            </Text>
          </View>
          <View style={{ flexDirection: 'row', gap: utils.spacing.lg }}>
            <View>
              <Text style={[styles.heading('xl'), { color: tokens.color.success }]}>
                {ownedCount}
              </Text>
              <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>
                Owned
              </Text>
            </View>
            <View>
              <Text style={[styles.heading('xl'), { color: tokens.color.secondary }]}>
                {lookingAtCount}
              </Text>
              <Text style={[styles.text('xs'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>
                Looking At
              </Text>
            </View>
          </View>
        </View>
      </View>

          {/* Ownership Split Visualization */}
          {portfolio.partners.length > 0 && (
            <View style={{ 
              marginBottom: utils.spacing.xl,
              backgroundColor: tokens.color.surface,
              borderRadius: utils.radius.lg,
              padding: utils.spacing.lg,
              borderWidth: 1,
              borderColor: tokens.color.border,
            }}>
              <Text style={[styles.heading('lg'), { marginBottom: utils.spacing.md }]}>
                Ownership Split
              </Text>
              <OwnershipBar
                segments={portfolio.partners.map((partner, index) => ({
                  label: getDisplayName(partner),
                  percentage: partner.ownership_percentage || 0,
                  isCurrentUser: partner.user_id === user?.id,
                }))}
                height={28}
                showLabels={true}
                showLegend={true}
              />
              {portfolio.partners.reduce((sum, p) => sum + (p.ownership_percentage || 0), 0) !== 100 && (
                <Text style={[styles.text('sm'), { 
                  color: '#F59E0B',
                  marginTop: utils.spacing.md,
                  fontStyle: 'italic',
                }]}>
                  ⚠️ Ownership doesn't add up to 100%. Set ownership in individual properties.
                </Text>
              )}
            </View>
          )}

          {/* Portfolio Details on Dashboard */}
          <View style={{ marginBottom: utils.spacing.xl }}>
          <DetailCard
            title="Portfolio Details"
            items={[
                {
                  label: 'Partners',
                  value: `${portfolio.partners.length} partner${portfolio.partners.length !== 1 ? 's' : ''}`,
                },
              {
                label: 'Created',
                value: new Date(portfolio.created_at).toLocaleDateString(),
              },
              {
                label: 'Last Updated',
                value: new Date(portfolio.updated_at).toLocaleDateString(),
              },
            ]}
            style={{ width: '100%' }}
            elevated
          />
        </View>

          {/* Financial Overview on Dashboard */}
          {financialSummary && (
            <View style={{ marginBottom: utils.spacing.xl }}>
              <DetailCard
                title="Financial Overview"
                items={[
                  {
                    label: 'Total Income',
                    value: `$${financialSummary.portfolio_totals.total_income.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                    color: tokens.color.success,
                    highlight: true,
                  },
                  {
                    label: 'Total Expenses',
                    value: `$${financialSummary.portfolio_totals.total_expenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                    color: tokens.color.error,
                    highlight: true,
                  },
                  {
                    label: 'Net Cash Flow',
                    value: `$${financialSummary.portfolio_totals.net_cash_flow.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                    color: financialSummary.portfolio_totals.net_cash_flow >= 0 ? tokens.color.success : tokens.color.error,
                    highlight: true,
                  },
                ]}
                style={{ width: '100%' }}
                elevated
              />
              <Text style={[styles.text('sm'), { 
                color: addColorOpacity(tokens.color.onSurface, 0.6), 
                fontStyle: 'italic', 
                paddingHorizontal: utils.spacing.md,
                textAlign: 'center',
                marginTop: utils.spacing.sm,
              }]}>
                Based on {financialSummary.portfolio_totals.entry_count} financial entries across {financialSummary.summary.length} {financialSummary.summary.length === 1 ? 'property' : 'properties'}
              </Text>
            </View>
          )}
        </>
      )}

      {/* Partners Tab */}
      {activeTab === 'partners' && (
          <SectionCard
            title={`Partners (${portfolio.partners.length})`}
            headerAction={
              <Button
                title="+ Invite"
                onPress={() => setInviteModalVisible(true)}
                variant="primary"
              />
            }
            style={{ width: '100%' }}
            elevated
          >
            {portfolio.partners.map((partner, index) => (
              <View 
                key={partner.user_id} 
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingVertical: utils.spacing.md,
                  borderBottomWidth: index < portfolio.partners.length - 1 ? 1 : 0,
                  borderBottomColor: addColorOpacity(tokens.color.border, 0.5),
                }}
              >
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: utils.spacing.sm }}>
                    <View style={{ flex: 1, marginRight: utils.spacing.md }}>
                      <Text style={[styles.text('base'), { 
                        fontWeight: utils.typography.weights.semibold, 
                        marginBottom: utils.spacing.xs,
                        color: partner.user_id === user?.id ? tokens.color.primary : tokens.color.onSurface,
                      }]}>
                        {getDisplayName(partner)} {partner.user_id === user?.id && '(You)'}
                      </Text>
                      <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.6) }]}>
                        Joined {new Date(partner.joined_at).toLocaleDateString()}
                      </Text>
                    </View>
                    {portfolio.partners.length > 1 && (
                      <Button
                        title="Remove"
                        onPress={() => handleRemovePartner(partner.user_id, getDisplayName(partner))}
                        variant="danger"
                        size="sm"
                      />
                    )}
                  </View>
                  {/* Ownership Bar */}
                  <SimpleOwnershipBar 
                    percentage={partner.ownership_percentage || 0}
                    color={partner.user_id === user?.id ? tokens.color.primary : tokens.color.secondary}
                    height={10}
                    showPercentage={true}
                  />
                  {partner.ownership_percentage === 0 && (
                    <Text style={[styles.text('xs'), { 
                      color: addColorOpacity(tokens.color.onSurface, 0.5),
                      fontStyle: 'italic',
                      marginTop: utils.spacing.xs,
                    }]}>
                      Set ownership in property details
                    </Text>
                  )}
                </View>
              </View>
            ))}
          </SectionCard>
      )}

      {/* Properties Tab */}
      {activeTab === 'properties' && (
          <View style={{ marginBottom: utils.spacing['2xl'] }}>
            <SectionCard
              title={`Properties (${properties.length})`}
              headerAction={
                <Button
                  title="+ Add Property"
                  onPress={() => setCreatePropertyModalVisible(true)}
                  variant="primary"
                />
              }
              style={{ width: '100%' }}
              elevated
            >

              {/* Portfolio Map - show all properties with coordinates */}
              {properties.some(p => p.latitude && p.longitude) && (
                <View style={{ marginBottom: utils.spacing.lg, borderRadius: utils.radius.lg, overflow: 'hidden' }}>
                  <PropertyMap
                    properties={properties}
                    height={responsive.isSmallMobile ? 250 : responsive.isMobile ? 300 : 350}
                    showControls={true}
                    onPropertyClick={(property) => {
                      const portfolioId = parseInt(id as string, 10);
                      if (!isNaN(portfolioId) && portfolioId > 0 && property.id) {
                        router.push(`/portfolios/${portfolioId}/properties/${property.id}`);
                      }
                    }}
                  />
                </View>
              )}

              {/* View Filter Buttons */}
              <View style={{ flexDirection: 'row', marginBottom: utils.spacing.md, gap: utils.spacing.sm, flexWrap: 'wrap' }}>
                {(['current', 'pipeline', 'full', 'historical'] as PortfolioView[]).map((view) => (
                  <Button
                    key={view}
                    title={VIEW_LABELS[view]}
                    onPress={() => setPropertyView(view)}
                    variant={propertyView === view ? 'primary' : 'outline'}
                    style={{ flex: 1, minWidth: 80 }}
                  />
                ))}
              </View>

              {loadingProperties ? (
                <View style={styles.emptyState}>
                  <ActivityIndicator size="small" color={tokens.color.primary} />
                  <Text style={styles.loadingText}>Loading properties...</Text>
                </View>
              ) : properties.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyText}>No properties found for this view.</Text>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onBackground, 0.6), textAlign: 'center' }]}>
                    Click "+ Add Property" to get started!
                  </Text>
                </View>
              ) : (
                <View style={{ gap: utils.spacing.md }}>
                  {properties.map((property) => (
                    <Card
                      key={property.id}
                      onPress={() => {
                        // Validate property ID to prevent path traversal
                        if (!property.id || typeof property.id !== 'number' || property.id <= 0) {
                          Alert.alert('Error', 'Invalid property');
                          return;
                        }
                        const portfolioId = parseInt(id as string, 10);
                        if (isNaN(portfolioId) || portfolioId <= 0) {
                          Alert.alert('Error', 'Invalid portfolio ID');
                          return;
                        }
                        router.push(`/portfolios/${portfolioId}/properties/${property.id}`);
                      }}
                      style={{
                        backgroundColor: tokens.color.surface,
                        borderWidth: 1,
                        borderColor: tokens.color.border,
                      }}
                      padding="md"
                    >
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: utils.spacing.md }}>
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: utils.spacing.sm, marginBottom: utils.spacing.xs, flexWrap: 'wrap' }}>
                            <Text 
                              style={[styles.text('base'), { 
                                fontWeight: utils.typography.weights.semibold,
                                color: tokens.color.onSurface,
                                flex: 1,
                              }]}
                              numberOfLines={1}
                            >
                              {formatAddress(property)}
                            </Text>
                            <StatusBadge status={property.status} size="sm" />
                          </View>
                          
                          {/* Property details row */}
                          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: utils.spacing.sm, alignItems: 'center' }}>
                            {property.property_value ? (
                              <Text style={[styles.text('sm'), { 
                                color: addColorOpacity(tokens.color.onSurface, 0.7),
                                fontWeight: utils.typography.weights.medium,
                              }]}>
                                ${property.property_value.toLocaleString()}
                              </Text>
                            ) : (
                              <Text style={[styles.text('sm'), { 
                                color: addColorOpacity(tokens.color.onSurface, 0.4),
                                fontStyle: 'italic',
                              }]}>
                                No value set
                              </Text>
                            )}
                            
                            {(property.city || property.state) && (
                              <>
                                <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.3) }]}>•</Text>
                                <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>
                                  {[property.city, property.state].filter(Boolean).join(', ')}
                                </Text>
                              </>
                            )}
                          </View>
                          
                          {property.purchase_date && (
                            <Text style={[styles.text('xs'), { 
                              color: addColorOpacity(tokens.color.onSurface, 0.5),
                              marginTop: utils.spacing.xs,
                            }]}>
                              Purchased {new Date(property.purchase_date).toLocaleDateString()}
                            </Text>
                          )}
                        </View>
                        
                        <Pressable
                          onPress={(e) => {
                            e.stopPropagation();
                            handleRemovePropertyClick(property.id, formatAddress(property));
                          }}
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 16,
                            backgroundColor: addColorOpacity(tokens.color.error, 0.1),
                            borderWidth: 1,
                            borderColor: addColorOpacity(tokens.color.error, 0.25),
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Text style={{ 
                            color: tokens.color.error, 
                            fontSize: 18, 
                            fontWeight: utils.typography.weights.bold,
                            lineHeight: 18,
                          }}>
                            ×
                          </Text>
                        </Pressable>
                      </View>
                    </Card>
                  ))}
                </View>
              )}
            </SectionCard>
          </View>
      )}

      {/* Discussion Tab */}
      {activeTab === 'discussion' && portfolio && (
        <View style={{ marginBottom: utils.spacing.xl }}>
          <CommentSection
            contextType="portfolio"
            contextId={portfolio.id}
            autoCreateThread={true}
            defaultThreadTitle="Portfolio Discussion"
            threadId={threadId ? parseInt(threadId, 10) : null}
          />
        </View>
      )}

      {/* Remove Property Modal */}
      <Modal
        visible={removePropertyModalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => {
          setRemovePropertyModalVisible(false);
          setPropertyToRemove(null);
        }}
      >
        <Pressable 
          style={styles.modalOverlay} 
          onPress={() => {
            setRemovePropertyModalVisible(false);
            setPropertyToRemove(null);
          }}
        >
          <Pressable style={[
            styles.modalContent,
            {
              maxWidth: responsive.isMobile ? '90%' : 500,
              width: '100%',
            }
          ]} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Remove Property</Text>
            <Text style={[styles.text('base'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.lg, lineHeight: bodySize * 1.5 }]}>
              Are you sure you want to remove "{propertyToRemove?.address}" from this portfolio?
              {'\n\n'}
              This will also remove all associated financial entries and ownership records. This action cannot be undone.
            </Text>
            <View style={styles.buttonRow}>
              <Button
                title="Cancel"
                onPress={() => {
                  setRemovePropertyModalVisible(false);
                  setPropertyToRemove(null);
                }}
                variant="outline"
                style={{ flex: 1, minWidth: 100 }}
              />
              <Button
                title={removingProperty ? 'Removing...' : 'Remove'}
                onPress={handleConfirmRemoveProperty}
                disabled={removingProperty}
                variant="danger"
                style={{ flex: 1, minWidth: 100 }}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Create Property Modal */}
      <Modal
        visible={createPropertyModalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setCreatePropertyModalVisible(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setCreatePropertyModalVisible(false)}>
          <ScrollView 
            contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', padding: utils.spacing.md }}
            keyboardShouldPersistTaps="handled"
          >
            <Pressable style={[
              styles.modalContent,
              {
                maxWidth: responsive.isMobile ? '100%' : 550,
                width: '100%',
                alignSelf: 'center',
              }
            ]} onPress={(e) => e.stopPropagation()}>
              <Text style={styles.modalTitle}>Add Property</Text>
              
              {/* Quick Address Search */}
              <View style={{ marginBottom: utils.spacing.lg }}>
                <View style={{ 
                  backgroundColor: addColorOpacity(tokens.color.primary, 0.08), 
                  padding: utils.spacing.md, 
                  borderRadius: utils.radius.md,
                  marginBottom: utils.spacing.sm,
                }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: utils.spacing.sm }}>
                    <Ionicons name="flash-outline" size={18} color={tokens.color.primary} />
                    <Text style={[styles.text('sm'), { color: tokens.color.primary, fontWeight: utils.typography.weights.semibold, marginLeft: utils.spacing.xs }]}>
                      Quick Add
                    </Text>
                  </View>
                  <Text style={[styles.text('sm'), { color: addColorOpacity(tokens.color.onSurface, 0.7), marginBottom: utils.spacing.sm }]}>
                    Paste an address from Google Maps, Zillow, or any listing to auto-fill all fields
                  </Text>
                  <AddressSearch
                    key={addressSearchKey}
                    placeholder="Paste or search for an address..."
                    onSelect={(result: AddressSearchResult) => {
                      // Auto-fill all fields from geocoding result
                      setNewPropertyStreetAddress(result.address?.street_address || result.formatted_address.split(',')[0] || '');
                      setNewPropertyCity(result.address?.city || '');
                      setNewPropertyState(result.address?.state_abbr || result.address?.state || '');
                      setNewPropertyZipCode(result.address?.zip_code || '');
                      setNewPropertyCounty(result.address?.county || '');
                      setNewPropertyLatitude(result.coordinates?.latitude || null);
                      setNewPropertyLongitude(result.coordinates?.longitude || null);
                      setShowManualEntry(true); // Show fields so user can verify/edit
                    }}
                  />
                </View>
                
                {/* Toggle for manual entry */}
                {!showManualEntry && !newPropertyStreetAddress && (
                  <Pressable 
                    onPress={() => setShowManualEntry(true)}
                    style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: utils.spacing.sm }}
                  >
                    <Ionicons name="create-outline" size={16} color={tokens.color.primary} />
                    <Text style={[styles.text('sm'), { color: tokens.color.primary, marginLeft: utils.spacing.xs }]}>
                      Or enter address manually
                    </Text>
                  </Pressable>
                )}
              </View>

              {/* Manual Entry Fields - shown if user clicks manual or after address search */}
              {(showManualEntry || newPropertyStreetAddress) && (
                <>
                  {/* Auto-filled indicator */}
                  {newPropertyLatitude && newPropertyLongitude && (
                    <View style={{ 
                      flexDirection: 'row', 
                      alignItems: 'center', 
                      backgroundColor: addColorOpacity(tokens.color.success, 0.1),
                      padding: utils.spacing.sm,
                      borderRadius: utils.radius.sm,
                      marginBottom: utils.spacing.md,
                    }}>
                      <Ionicons name="checkmark-circle" size={16} color={tokens.color.success} />
                      <Text style={[styles.text('sm'), { color: tokens.color.success, marginLeft: utils.spacing.xs }]}>
                        Address verified • Coordinates captured
                      </Text>
                    </View>
                  )}

                  <Input
                    placeholder="Street Address *"
                    value={newPropertyStreetAddress}
                    onChangeText={(text) => {
                      if (text.length <= 500) {
                        setNewPropertyStreetAddress(text);
                      }
                    }}
                    autoCapitalize="words"
                    maxLength={500}
                    style={{ marginBottom: utils.spacing.md }}
                  />

                  <View style={{ flexDirection: 'row', gap: utils.spacing.md, marginBottom: utils.spacing.md }}>
                    <View style={{ flex: 2 }}>
                      <Input
                        placeholder="City"
                        value={newPropertyCity}
                        onChangeText={setNewPropertyCity}
                        autoCapitalize="words"
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Input
                        placeholder="State"
                        value={newPropertyState}
                        onChangeText={setNewPropertyState}
                        autoCapitalize="characters"
                        maxLength={2}
                      />
                    </View>
                  </View>

                  <View style={{ flexDirection: 'row', gap: utils.spacing.md, marginBottom: utils.spacing.md }}>
                    <View style={{ flex: 1 }}>
                      <Input
                        placeholder="ZIP Code"
                        value={newPropertyZipCode}
                        onChangeText={setNewPropertyZipCode}
                        keyboardType="numeric"
                        maxLength={10}
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Input
                        placeholder="County"
                        value={newPropertyCounty}
                        onChangeText={setNewPropertyCounty}
                        autoCapitalize="words"
                      />
                    </View>
                  </View>

                  <Input
                    placeholder="Property Value (optional)"
                    value={newPropertyValue}
                    onChangeText={(text) => {
                      const sanitized = text.replace(/[^0-9.]/g, '').slice(0, 20);
                      setNewPropertyValue(sanitized);
                    }}
                    keyboardType="numeric"
                    maxLength={20}
                    style={{ marginBottom: utils.spacing.md }}
                  />

                  <View style={{ marginBottom: utils.spacing.lg }}>
                    <Text style={[styles.text('base'), { fontWeight: utils.typography.weights.semibold, marginBottom: utils.spacing.sm }]}>Status:</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: utils.spacing.sm }}>
                      {(['research', 'under_contract', 'owned', 'listed_for_sale', 'sold'] as Property['status'][]).map((status) => (
                        <Button
                          key={status}
                          title={STATUS_LABELS[status]}
                          onPress={() => setNewPropertyStatus(status)}
                          variant={newPropertyStatus === status ? 'primary' : 'outline'}
                          style={{ flex: 1, minWidth: 90 }}
                        />
                      ))}
                    </View>
                  </View>
                </>
              )}

              <View style={styles.buttonRow}>
                <Button
                  title="Cancel"
                  onPress={() => {
                    setCreatePropertyModalVisible(false);
                    setNewPropertyAddress('');
                    setNewPropertyStreetAddress('');
                    setNewPropertyCity('');
                    setNewPropertyState('');
                    setNewPropertyZipCode('');
                    setNewPropertyCounty('');
                    setNewPropertyLatitude(null);
                    setNewPropertyLongitude(null);
                    setNewPropertyValue('');
                    setNewPropertyStatus('research');
                    setShowManualEntry(false);
                    setAddressSearchKey(prev => prev + 1);
                  }}
                  variant="outline"
                  style={{ flex: 1, minWidth: 100 }}
                />
                <Button
                  title={creatingProperty ? 'Creating...' : 'Create'}
                  onPress={handleCreateProperty}
                  disabled={creatingProperty || !newPropertyStreetAddress.trim()}
                  variant="primary"
                  style={{ flex: 1, minWidth: 100 }}
                />
              </View>
            </Pressable>
          </ScrollView>
        </Pressable>
      </Modal>

      {/* Invite Partner Modal */}
      <Modal
        visible={inviteModalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setInviteModalVisible(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setInviteModalVisible(false)}
        >
          <Pressable style={[
            styles.modalContent,
            {
              maxWidth: responsive.isMobile ? '90%' : 500,
              width: '100%',
            }
          ]} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Invite Partner</Text>
            
            <Text style={[styles.text('base'), { color: addColorOpacity(tokens.color.onSurface, 0.8), marginBottom: utils.spacing.lg, lineHeight: bodySize * 1.5 }]}>
              Partner must have an account. They can request one if needed.
              {'\n\n'}Note: Ownership is set at the property level, not portfolio level.
            </Text>

            <Input
              placeholder="Email *"
              value={inviteEmail}
              onChangeText={(text) => {
                // Limit input length for security
                if (text.length <= 255) {
                  setInviteEmail(text);
                }
              }}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              autoFocus
              maxLength={255}
              style={{ marginBottom: utils.spacing.md }}
            />

            <View style={styles.buttonRow}>
              <Button
                title="Cancel"
                onPress={() => {
                  setInviteModalVisible(false);
                  setInviteEmail('');
                }}
                variant="outline"
                style={{ flex: 1, minWidth: 100 }}
              />
              <Button
                title={inviting ? 'Inviting...' : 'Invite'}
                onPress={handleInvitePartner}
                disabled={inviting || !inviteEmail.trim()}
                variant="primary"
                style={{ flex: 1, minWidth: 100 }}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </PageContainer>
  );
}


